import React from 'react';
import { GraduationCapIcon, CheckIcon, LockIcon } from 'lucide-react';
import { areaStudyOptions, FilterOption } from '../data/filterOptions';
import { TrackingNoticeLinks } from './TrackingNoticeLinks';

// Visual shell for the /request-information/v3 template: the marketing
// copy stays pinned on the left while every question is asked inside a
// single persistent card on the right.

export interface SplitCardFormValues {
  areaOfStudy: number;
  program: number;
  highestEducation: string;
  hsGraduationYear: string;
  startDate: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  consent: boolean;
}
export interface RequestInfoSplitCardProps {
  badge: string;
  headline: string;
  headlineAccent: string;
  subhead: string;
  features: string[];
  // Flow position. `step` matches the shared flow (0 = program selection,
  // 1 = education, 2 = grad year, 3 = timeframe, 5 = address, 6 = contact).
  // `stage` splits step 0 into area (0) then program (1).
  step: number;
  stage: number;
  formData: SplitCardFormValues;
  availablePrograms: FilterOption[];
  years: number[];
  error: string;
  tcpaLoading: boolean;
  tcpaError: string;
  tcpaDisclaimerHtml: string | null;
  onFieldChange: (field: keyof SplitCardFormValues, value: string | number | boolean) => void;
  onAreaSelect: (area: number) => void;
  onZipChange: (zip: string) => void;
  onPhoneChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onContinue: () => void;
  onBack: () => void;
}

// Visual Arts & Graphic Design (12) isn't offered on this template.
const HIDDEN_AREA_VALUES = [12];
const AREA_TILES = areaStudyOptions.filter(option => !HIDDEN_AREA_VALUES.includes(option.value));
const STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
const START_OPTIONS: {
  value: string;
  label: string;
}[] = [{
  value: 'immediately',
  label: 'Immediately'
}, {
  value: '1-3-months',
  label: 'Within 1–3 months'
}, {
  value: '3-6-months',
  label: 'Within 3–6 months'
}, {
  value: '6-12-months',
  label: 'Within 6–12 months'
}, {
  value: '1-year-plus',
  label: 'More than a year out'
}, {
  value: 'undecided',
  label: 'Undecided'
}];
const EDUCATION_OPTIONS: {
  value: string;
  label: string;
}[] = [{
  value: 'high-school',
  label: 'High School Diploma/GED'
}, {
  value: 'some-college',
  label: 'Some College'
}, {
  value: 'associates',
  label: "Associate's Degree"
}, {
  value: 'bachelors',
  label: "Bachelor's Degree"
}, {
  value: 'masters',
  label: "Master's Degree"
}, {
  value: 'doctorate',
  label: 'Doctorate'
}, {
  value: 'some-high-school',
  label: 'Some High School'
}];
const FALLBACK_CONSENT = 'By checking this box and clicking "See My Matches," I provide my electronic signature and express written consent for findonlinedegrees.org and up to four (4) partner schools to contact me at the phone number and email provided above — including by SMS/text messages and calls using an automatic telephone dialing system or an artificial/prerecorded voice — regarding educational opportunities. Message and data rates may apply. Message frequency varies. Consent is not a condition of enrollment or purchase. I can revoke consent at any time.';
const CARD_COPY: Record<string, {
  title: string;
  subtitle: string;
}> = {
  area: {
    title: "Let's Get Started",
    subtitle: 'Select your area of interest'
  },
  program: {
    title: 'Narrow it down',
    subtitle: 'Pick the program closest to your interest'
  },
  education: {
    title: 'Your background',
    subtitle: 'This helps us match you to programs you qualify for'
  },
  gradYear: {
    title: 'Almost there',
    subtitle: 'What year did you graduate high school?'
  },
  timing: {
    title: 'Timing',
    subtitle: 'When would you like to start classes?'
  },
  address: {
    title: 'Where are you located?',
    subtitle: 'Used to match you with programs available in your state'
  },
  contact: {
    title: 'Almost done',
    subtitle: 'Where should schools reach you?'
  }
};
const selectClasses = (filled: boolean) => `w-full px-4 py-3 bg-white border rounded-xl text-gray-900 appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-[#e8594f]/30 ${filled ? 'border-[#e8594f]' : 'border-gray-200 hover:border-gray-300'}`;
const inputClasses = 'w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 transition-colors placeholder:text-gray-400 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/30 hover:border-gray-300';
const chevronStyle = {
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
  backgroundPosition: 'right 0.75rem center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1.25em 1.25em',
  paddingRight: '2.5rem'
};
const FieldLabel = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-489" data-mp="3k4z5y4j25pab">
    {children} <span className="text-[#e8594f]" data-id="element-490" data-mp="3f7641nosfhfu">*</span>
  </span>;
export function RequestInfoSplitCard({
  badge,
  headline,
  headlineAccent,
  subhead,
  features,
  step,
  stage,
  formData,
  availablePrograms,
  years,
  error,
  tcpaLoading,
  tcpaError,
  tcpaDisclaimerHtml,
  onFieldChange,
  onAreaSelect,
  onZipChange,
  onPhoneChange,
  onContinue,
  onBack
}: RequestInfoSplitCardProps) {
  const screen = step === 0 ? stage === 0 ? 'area' : 'program' : step === 1 ? 'education' : step === 2 ? 'gradYear' : step === 3 ? 'timing' : step === 5 ? 'address' : 'contact';
  const displayStep = screen === 'area' ? 1 : screen === 'program' ? 2 : screen === 'education' ? 3 : screen === 'gradYear' ? 4 : screen === 'timing' ? 5 : screen === 'address' ? 6 : 7;
  const canGoBack = !(step === 0 && stage === 0);
  const isFinalStep = screen === 'contact';
  const copy = CARD_COPY[screen];
  return <div className="min-h-screen bg-[#f4f5f7] flex flex-col" data-id="element-491" data-mp="2f13mws4h5j7a">
      {/* Advertising disclosure */}
      <div className="bg-gray-100 border-b border-gray-200 py-2.5 px-4" data-id="element-492" data-mp="2jz5b1rgrc8pm">
        <p className="text-center text-xs text-gray-600" data-id="element-493" data-mp="37p7z7whjwuiy">
          We earn commissions from schools listed on this site, which influences
          how listings are presented.{' '}
          <a href="/terms" className="underline hover:text-gray-900" data-id="element-494" data-mp="2yo1grhvcd9n2">
            Advertising Disclosure
          </a>
        </p>
      </div>

      {/* Brand bar */}
      <header className="bg-white border-b border-gray-200 py-4 px-4" data-id="element-495" data-mp="32qy3nkrwzrwt">
        <a href="/" className="flex items-center justify-center gap-2.5" data-id="element-496" data-mp="1n6rygyny8nnc">
          <span className="w-9 h-9 rounded-lg bg-[#e8594f] flex items-center justify-center" data-id="element-497" data-mp="1razl8qaujtix">
            <GraduationCapIcon className="w-5 h-5 text-white" aria-hidden="true" data-id="element-498" data-mp="1jpdxsuzpe4m3" />
          </span>
          <span className="text-xl font-bold text-gray-900 tracking-tight" data-id="element-499" data-mp="1eqar7642rqc0">
            FindOnlineDegrees
          </span>
        </a>
      </header>

      <main className="flex-1 px-4 py-8 md:py-16" data-id="element-500" data-mp="1tm8v5ak8pyys">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-501" data-mp="1oqjrow53nxx7">
          {/* Marketing column — below the card on mobile */}
          <div className="order-2 lg:order-1" data-id="element-502" data-mp="w0upifgnlru0">
            <div className="text-center lg:text-left" data-id="element-503" data-mp="w1w4uw4pd0hj">
              <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3.5 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-504" data-mp="1apqvm6z5gurz">
                <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-505" data-mp="15y8u9ht7ss6h" />
                {badge}
              </div>
            </div>
            <h1 className="text-center lg:text-left text-4xl sm:text-5xl font-extrabold text-gray-900 leading-[1.05] tracking-tight mb-6" data-id="element-506" data-mp="i7bq4r7nuz5s">
              {headline}
              <span className="block text-[#e8594f]" data-id="element-507" data-mp="3nxpsz775rzzu">{headlineAccent}</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-7 max-w-md" data-id="element-508" data-mp="3swk89z5x36f7">
              {subhead}
            </p>
            <ul className="space-y-3" data-id="element-509" data-mp="1nwqtuass3cx">
              {features.map(feature => <li key={feature} className="flex items-center gap-3 text-gray-800" data-id="element-510" data-mp="5scr9g1n6ikr">
                  <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-511" data-mp="ap37c3dg5e66" />
                  <span data-id="element-512" data-mp="fno9i8c9e7x9">{feature}</span>
                </li>)}
            </ul>
          </div>

          {/* Question card — first on mobile so it lands above the fold */}
          <div className="order-1 lg:order-2 bg-white rounded-2xl shadow-[0_10px_40px_-12px_rgba(15,23,42,0.18)] p-6 sm:p-8" data-id="element-513" data-mp="pf23y1pcshjj">
            <div className="text-center mb-6" data-id="element-514" data-mp="thyssa5hl9vx">
              <p className="font-mono text-xs tracking-[0.18em] text-[#e8594f] mb-2" data-id="element-515" data-mp="yf79e6azl5v2">
                STEP {displayStep} OF 7
              </p>
              <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-516" data-mp="2iyav0noeqz93">
                {copy.title}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-517" data-mp="2dx3jqen23hxo">{copy.subtitle}</p>
            </div>

            {screen === 'area' && <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="group" aria-label="Area of interest" data-id="element-518" data-mp="24xq9hjr3ypbw">
                {AREA_TILES.map(option => {
              const selected = formData.areaOfStudy === option.value;
              return <button key={option.value} type="button" onClick={() => onAreaSelect(option.value)} aria-pressed={selected} className={`text-center px-3 py-4 rounded-xl border transition-all ${selected ? 'border-[#e8594f] bg-[#e8594f]/5 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`} data-id="element-519" data-mp="2xmxf12ozcf6n">
                      <span className={`mx-auto mb-2 flex w-4 h-4 items-center justify-center rounded border ${selected ? 'border-[#e8594f] bg-[#e8594f]' : 'border-gray-300 bg-white'}`} aria-hidden="true" data-id="element-520" data-mp="2og02ssjsz5tl">
                        {selected && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-521" data-mp="3cbumg94dr2za" />}
                      </span>
                      <span className="block text-sm font-semibold text-gray-900 leading-snug" data-id="element-522" data-mp="37c8dwczhyawt">
                        {option.label}
                      </span>
                    </button>;
            })}
              </div>}

            {screen === 'program' && <label className="block" data-id="element-523" data-mp="3v1ud30fvcq4t">
                <FieldLabel data-id="element-524" data-mp="3q5c6dv0zahpu">Program of Interest</FieldLabel>
                <select value={formData.program || ''} onChange={e => onFieldChange('program', Number(e.target.value))} className={selectClasses(Boolean(formData.program))} style={chevronStyle} data-id="element-525" data-mp="3q2r7nevz8hwb">
                  <option value="" data-id="element-526" data-mp="4ka30hdh10ca">Select a program</option>
                  {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-527" data-mp="8nzp906ob5u5">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'education' && <label className="block" data-id="element-528" data-mp="3h39jxn88h8rw">
                <FieldLabel data-id="element-529" data-mp="3h03wecf96eya">Highest Education Level</FieldLabel>
                <select value={formData.highestEducation} onChange={e => onFieldChange('highestEducation', e.target.value)} className={selectClasses(Boolean(formData.highestEducation))} style={chevronStyle} data-id="element-530" data-mp="3m1kuy1a9lpbp">
                  <option value="" data-id="element-531" data-mp="1ilpm4ze6kwoc">Select one</option>
                  {EDUCATION_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-532" data-mp="1ds3ifwpzw34o">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'gradYear' && <label className="block" data-id="element-533" data-mp="21j8yoczukxfi">
                <FieldLabel data-id="element-534" data-mp="21i7m2436o2vo">HS Graduation Year</FieldLabel>
                <select value={formData.hsGraduationYear} onChange={e => onFieldChange('hsGraduationYear', e.target.value)} className={selectClasses(Boolean(formData.hsGraduationYear))} style={chevronStyle} data-id="element-535" data-mp="1wjnyjz8xyj17">
                  <option value="" data-id="element-536" data-mp="h3ff86ixukfb">Select year</option>
                  {years.map(year => <option key={year} value={year} data-id="element-537" data-mp="c3t1lvz7725z">
                      {year}
                    </option>)}
                  <option value="before-1965" data-id="element-538" data-mp="2nsza9rstbq2i">Before 1965</option>
                </select>
              </label>}

            {screen === 'timing' && <label className="block" data-id="element-539" data-mp="2wruewzg7ks3p">
                <FieldLabel data-id="element-540" data-mp="2wqt2aqjjnxjv">Class Start Timeframe</FieldLabel>
                <select value={formData.startDate} onChange={e => onFieldChange('startDate', e.target.value)} className={selectClasses(Boolean(formData.startDate))} style={chevronStyle} data-id="element-541" data-mp="31j2njt9ps9ft">
                  <option value="" data-id="element-542" data-mp="3ggcyn1ig3db0">Select timeframe</option>
                  {START_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-543" data-mp="3ki6sapu83h1o">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'address' && <div className="space-y-4" data-id="element-544" data-mp="1q5qhrcue9gr0">
                <label className="block" data-id="element-545" data-mp="usxxxosgtzea">
                  <FieldLabel data-id="element-546" data-mp="uro0n1tfsv8p">Street Address</FieldLabel>
                  <input type="text" value={formData.streetAddress} onChange={e => onFieldChange('streetAddress', e.target.value)} placeholder="123 Main St" autoComplete="street-address" className={inputClasses} data-id="element-547" data-mp="qsnjrouis2oc" />
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-548" data-mp="73wl7pclo237">
                  <label className="block" data-id="element-549" data-mp="75hfreywzl12">
                    <FieldLabel data-id="element-550" data-mp="lsj39ytm37y4">City</FieldLabel>
                    <input type="text" value={formData.city} onChange={e => onFieldChange('city', e.target.value)} autoComplete="address-level2" className={inputClasses} data-id="element-551" data-mp="ludmn8p8is03" />
                  </label>
                  <label className="block" data-id="element-552" data-mp="3kgyneerafzq9">
                    <FieldLabel data-id="element-553" data-mp="1zzpiezi3vcai">State</FieldLabel>
                    <select value={formData.state} onChange={e => onFieldChange('state', e.target.value)} className={selectClasses(Boolean(formData.state))} style={chevronStyle} data-id="element-554" data-mp="1zyxl9k8v16i4">
                      <option value="" data-id="element-555" data-mp="1uzl0d51ju9i6">State</option>
                      {STATES.map(abbr => <option key={abbr} value={abbr} data-id="element-556" data-mp="2ip5ixmtsj4qx">
                          {abbr}
                        </option>)}
                    </select>
                  </label>
                  <label className="block" data-id="element-557" data-mp="29so68f5s2yt2">
                    <FieldLabel data-id="element-558" data-mp="2eovg2j1aouga">ZIP Code</FieldLabel>
                    <input type="text" inputMode="numeric" maxLength={5} value={formData.zipCode} onChange={e => onZipChange(e.target.value.replace(/\D/g, '').slice(0, 5))} autoComplete="postal-code" className={inputClasses} data-id="element-559" data-mp="2errcu41epd35" />
                  </label>
                </div>
              </div>}

            {screen === 'contact' && <div className="space-y-4" data-id="element-560" data-mp="2r8imkz9v6iol">
                <div className="grid grid-cols-2 gap-3" data-id="element-561" data-mp="2r7hcax0oa07x">
                  <label className="block" data-id="element-562" data-mp="2m8wait5rv8hd">
                    <FieldLabel data-id="element-563" data-mp="2m9z2l4etzgyv">First Name</FieldLabel>
                    <input type="text" value={formData.firstName} onChange={e => onFieldChange('firstName', e.target.value)} autoComplete="given-name" className={inputClasses} data-id="element-564" data-mp="2hacjwfg8h8io" />
                  </label>
                  <label className="block" data-id="element-565" data-mp="aibttlcpxe0b">
                    <FieldLabel data-id="element-566" data-mp="ahah7cg20jgh">Last Name</FieldLabel>
                    <input type="text" value={formData.lastName} onChange={e => onFieldChange('lastName', e.target.value)} autoComplete="family-name" className={inputClasses} data-id="element-567" data-mp="6jcssbu3y1e5" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-568" data-mp="2271nkqs9eexx">
                  <label className="block" data-id="element-569" data-mp="226jnfj2h7gkq">
                    <FieldLabel data-id="element-570" data-mp="15h25o5q7y42o">Email Address</FieldLabel>
                    <input type="email" value={formData.email} onChange={e => onFieldChange('email', e.target.value)} autoComplete="email" className={inputClasses} data-id="element-571" data-mp="15in2k2009z3p" />
                  </label>
                  <label className="block" data-id="element-572" data-mp="1je7bc4m0t0xe">
                    <FieldLabel data-id="element-573" data-mp="2pvm16ftzop9y">Phone Number</FieldLabel>
                    <input type="tel" value={formData.phone} onChange={onPhoneChange} placeholder="(555) 555-5555" autoComplete="tel" className={inputClasses} data-id="element-574" data-mp="2uvqiu96mvq1r" />
                  </label>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4" data-id="element-575" data-mp="2fd1kgmre4vb0">
                  {tcpaLoading ? <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-576" data-mp="2ainks85p420j">
                      <span className="w-4 h-4 border-2 border-gray-200 border-t-[#e8594f] rounded-full animate-spin" aria-hidden="true" data-id="element-577" data-mp="2p83cbqd1wbha" />
                      Loading consent terms…
                    </div> : tcpaError ? <p className="text-sm text-red-600" data-id="element-578" data-mp="2u90rdu2rkpek">{tcpaError}</p> : <label className="flex items-start gap-3 cursor-pointer" data-id="element-579" data-mp="13w1uhxsimsk5">
                      <input type="checkbox" checked={formData.consent} onChange={e => onFieldChange('consent', e.target.checked)} className="mt-0.5 w-4 h-4 flex-shrink-0 rounded border-gray-300 text-[#e8594f] focus:ring-[#e8594f]" data-id="element-580" data-mp="13y4unandu22l" />
                      {tcpaDisclaimerHtml ? <span className="text-xs text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{
                  __html: tcpaDisclaimerHtml
                }} data-id="element-581" data-mp="fz5imnkjydox" /> : <span className="text-xs text-gray-600 leading-relaxed" data-id="element-582" data-mp="b25am74570v6">
                          {FALLBACK_CONSENT} I also agree to the{' '}
                          <a href="/privacy-policy" className="underline hover:text-gray-900" data-id="element-583" data-mp="3t9ut483okmlq">
                            Privacy Policy
                          </a>{' '}
                          and{' '}
                          <a href="/terms" className="underline hover:text-gray-900" data-id="element-584" data-mp="1wf8528lcdec5">
                            Terms of Use
                          </a>
                          .
                        </span>}
                    </label>}
                </div>
              </div>}

            {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3" data-id="element-585" data-mp="2q2t9o9ys5nd9">
                <p className="text-sm text-red-600" data-id="element-586" data-mp="2v1342icokjxt">{error}</p>
              </div>}

            <div className={`mt-6 ${canGoBack ? 'flex gap-3' : ''}`} data-id="element-587" data-mp="3iqxc8mm21kbq">
              {canGoBack && <button type="button" onClick={onBack} className="px-6 py-3.5 rounded-xl border border-gray-200 bg-white font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300" data-id="element-588" data-mp="3nnnv1hp8uu0u">
                  ← Back
                </button>}
              <button type="button" onClick={onContinue} className="flex-1 w-full px-6 py-3.5 rounded-xl bg-[#e8594f] font-bold text-white transition-colors hover:bg-[#d64a41] focus:outline-none focus:ring-2 focus:ring-[#e8594f]/40" data-id="element-589" data-mp="22c5ncjo4av76">
                {isFinalStep ? 'See My Matches →' : 'Continue →'}
              </button>
            </div>

            {isFinalStep && <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-500" data-id="element-590" data-mp="1zrr30a469hao">
                <LockIcon className="w-3 h-3" aria-hidden="true" data-id="element-591" data-mp="1c254zpzjd849" />
                Your information is secure and confidential
              </p>}
          </div>
        </div>
      </main>

      <footer className="py-8 px-4" data-id="element-592" data-mp="1lsztz2462lte">
        <p className="text-center font-mono text-xs text-gray-500" data-id="element-593" data-mp="1h0q75uycm1vc">
          findonlinedegrees.org &nbsp;·&nbsp;
          <a href="/privacy-policy" className="underline hover:text-gray-700" data-id="element-594" data-mp="tavt5a2am79i">
            Privacy Policy
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/terms" className="underline hover:text-gray-700" data-id="element-595" data-mp="obj7gsn5r68i">
            Terms of Use
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/cookie-policy" className="underline hover:text-gray-700" data-id="element-596" data-mp="1i1s2cpv36bcu">
            Cookie Policy
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/ca-notice" className="underline hover:text-gray-700" data-id="element-597" data-mp="1mtt9jxdedbw0">
            Do Not Sell My Info
          </a>
        </p>
        <p className="mt-3 text-center text-xs text-gray-500 max-w-2xl mx-auto" data-id="element-598" data-mp="1rtpdjwm3h3bk">
          This page uses cookies and pixels from third parties, including Meta,
          for advertising and measurement. See our{' '}
          <a href="/cookie-policy" className="underline hover:text-gray-700" data-id="element-599" data-mp="1420iw9lsw2rg">
            Cookie Policy
          </a>{' '}
          for opt-out options.
        </p>
      </footer>
    </div>;
}